import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, PlaneTakeoff } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function News() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (typeof window !== "undefined") window.__prerender_ready = false;
    api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  // News uses static SEO meta (not fetched), so we can signal ready
  // as soon as the items request resolves (success or failure).
  useEffect(() => {
    if (items !== null) {
      const t = setTimeout(() => {
        if (typeof window !== "undefined") window.__prerender_ready = true;
      }, 50);
      return () => clearTimeout(t);
    }
  }, [items]);

  return (
    <>
      <SEO title="News & Updates" description="Latest news, announcements and updates from AOA Somalia." path="/news" />
      <section className="bg-[hsl(var(--aoa-navy))] text-white" data-testid="news-hero">
        <div className="aoa-container py-16 md:py-20">
          <span className="aoa-eyebrow text-[hsl(var(--aoa-sky-light))]">News</span>
          <h1 className="aoa-h1 mt-4">News &amp; Updates</h1>
          <p className="mt-4 text-lg text-slate-200/90 max-w-2xl">
            Announcements, position papers and stakeholder updates from AOA Somalia.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="aoa-container py-16">
          {items === null ? (
            <div className="py-20 text-center text-slate-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="mx-auto max-w-xl text-center py-16" data-testid="news-empty">
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[hsl(var(--aoa-gray-bg))]">
                <PlaneTakeoff className="h-9 w-9 text-[hsl(var(--aoa-royal))]" />
              </div>
              <p className="mt-6 font-display text-xl font-semibold text-[hsl(var(--aoa-navy))]">
                News and updates will be published here.
              </p>
              <p className="mt-2 text-sm text-slate-600">Check back soon for announcements, position papers and stakeholder updates.</p>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {items.map((n, i) => (
                <motion.article
                  key={n.id || n.slug}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="aoa-card flex flex-col"
                  data-testid={`news-card-${n.slug}`}
                >
                  {n.cover_image && (
                    <Link to={`/news/${n.slug}`} className="block -m-6 mb-5 overflow-hidden rounded-t-xl aspect-[16/9]">
                      <img src={n.cover_image} alt="" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                    </Link>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="h-3.5 w-3.5" />
                    {n.published_at ? new Date(n.published_at).toLocaleDateString() : ""}
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">
                    <Link to={`/news/${n.slug}`} className="hover:text-[hsl(var(--aoa-royal))]">{n.title}</Link>
                  </h3>
                  {n.excerpt && <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{n.excerpt}</p>}
                  <Link to={`/news/${n.slug}`} className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-[hsl(var(--aoa-royal))]">
                    Read more <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
